/**
 * Admin Dashboard Component
 *
 * Main dashboard/homepage for authenticated admin users.
 * This is the initial page shown after login.
 *
 * Consists of three main sections:
 * 1. Dashboard Cards - Statistical overview cards
 * 2. Dashboard Table - One row per employee (invitation pipeline statuses + resubmission flow)
 * 3. Notification Area - Alerts and action items
 */
import React,{useState,useEffect,useMemo} from 'react';
import DashboardCards from './dashboard/DashboardCards';
import DashboardTable from './dashboardtable/DashboardTable';
import type { TableRow } from './dashboardtable/DashboardTable';
import NotificationArea,{Notification} from './notification area/NotificationArea';
import { API_BASE_URL } from '../../../config';
import { nowClockLabelJst } from '../../../utils/japanTime';
import { useAdminChatUnread } from '../../../contexts/AdminChatUnreadContext';
import './Dashboard.css';

export interface DashboardProps {
  /** Called when user clicks the "email send error" notification */
  onNavigateToInvitationErrors?: () => void;
  /** Called when user clicks the "registered not yet sent invitation" notification — open 招待を作成 */
  onNavigateToCreateInvitation?: () => void;
  /** Called when user clicks the "new submissions to check" notification — open 書類管理 */
  onNavigateToDocuments?: () => void;
  /** Called when user clicks the unread chat messages notification */
  onNavigateToMessages?: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({
  onNavigateToInvitationErrors,
  onNavigateToCreateInvitation,
  onNavigateToDocuments,
  onNavigateToMessages,
}) => {

  const chatUnread = useAdminChatUnread();

  const [unreadBounceCount, setUnreadBounceCount] = useState<number>(0);
  const [registeredCount, setRegisteredCount] = useState<number>(0);
  const [submissionsToCheckCount, setSubmissionsToCheckCount] = useState<number>(0);
  const [dismissedNotificationIds, setDismissedNotificationIds] = useState<string[]>([]);
  const [dashboardTableData, setDashboardTableData] = useState<TableRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/employees/dashboard-list`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Failed to load dashboard list'))))
      .then((rows: TableRow[]) => {
        if (!cancelled) setDashboardTableData(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setDashboardTableData([]);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const loadBounces = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/bounces/count`);
        if (res.ok) {
          const data = await res.json();
          const n = typeof data.count === 'number' ? data.count : Number(data.count) || 0;
          setUnreadBounceCount(n);
        } else {
          setUnreadBounceCount(0);
        }
      } catch {
        setUnreadBounceCount(0);
      }
    };
    loadBounces();
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/employees/dashboard-counts`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Failed to load counts'))))
      .then((data: { registered?: number; submissionsToCheck?: number }) => {
        if (!cancelled) {
          const reg = typeof data.registered === 'number' ? data.registered : Number(data.registered) || 0;
          setRegisteredCount(reg);
          const sub = typeof data.submissionsToCheck === 'number' ? data.submissionsToCheck : Number(data.submissionsToCheck) || 0;
          setSubmissionsToCheckCount(sub);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRegisteredCount(0);
          setSubmissionsToCheckCount(0);
        }
      });
    return () => { cancelled = true; };
  }, []);


  const notifications: Notification[] = useMemo(() => {
    const jstNow = nowClockLabelJst();
    const list: Notification[] = [];
    if (unreadBounceCount > 0) {
      list.push({
        id: 'bounce-alert',
        date: jstNow.date,
        time: jstNow.time,
        message: `メール送信エラーが${unreadBounceCount}件あります。確認が必要です。`,
        OnClick: onNavigateToInvitationErrors,
      });
    }
    if (registeredCount > 0) {
      list.push({
        id: 'registered-not-sent',
        date: jstNow.date,
        time: jstNow.time,
        message: `招待メールがまだ送信されていない従業員が${registeredCount}名います。`,
        OnClick: onNavigateToCreateInvitation,
      });
    }
    if (submissionsToCheckCount > 0) {
      list.push({
        id: 'submissions-to-check',
        date: jstNow.date,
        time: jstNow.time,
        message: `確認待ちの提出が${submissionsToCheckCount}件あります。`,
        OnClick: onNavigateToDocuments,
      });
    }
    if (chatUnread.count > 0) {
      list.push({
        id: 'chat-unread',
        date: jstNow.date,
        time: jstNow.time,
        message: `未読メッセージが${chatUnread.count}件あります。`,
        OnClick: onNavigateToMessages,
      });
    }
    return list;
  }, [unreadBounceCount, registeredCount, submissionsToCheckCount, chatUnread.count, onNavigateToInvitationErrors, onNavigateToCreateInvitation, onNavigateToDocuments, onNavigateToMessages]);

    const visibleNotifications = useMemo(
    () => notifications.filter((n) => !dismissedNotificationIds.includes(n.id)),
    [notifications, dismissedNotificationIds]
  );

  const handleDismiss = (id: string) =>{
    setDismissedNotificationIds((prev) => [...prev, id]);
  };

  const handleClearAll = () =>{
    setDismissedNotificationIds((prev) => [...prev, ...notifications.map(n => n.id)]);
  }

  return (
    <div className="admin-dashboard">
      {/* Section 1: Dashboard Cards */}
      <DashboardCards />

      {/* Section 2: Dashboard Table */}
      <DashboardTable data={dashboardTableData} />

      {/* Section 3: Notification Area */}
      <NotificationArea 
      notifications={visibleNotifications} 
      onDismiss={handleDismiss} 
      onClearAll={handleClearAll} />
    </div>
  );
};

export default Dashboard;
